<div class="ical">

  <header class="ical__head">
    <div>
      <h1 class="ical__title">
        <i class="fa-solid fa-calendar-days"></i>
        {{ 'INSPECTION.CALENDAR.TITLE' | translate }}
      </h1>
      <p class="ical__sub">{{ 'INSPECTION.CALENDAR.SUBTITLE' | translate }}</p>
    </div>
  </header>

  <!-- Ce que le serveur laisse voir : le périmètre, ou son absence. -->
  @if (scope(); as s) {
    <app-inspection-scope-summary class="ical__scope" [scope]="s"></app-inspection-scope-summary>
  }

  <!-- ─── Facettes ─────────────────────────────────────────────────── -->
  <div class="ical__filters">
    <label class="ical-filter">
      <span class="ical-filter__label">{{ 'CALENDAR.FILTER.PROFESSOR' | translate }}</span>
      <select [(ngModel)]="filterProfessorId" (ngModelChange)="applyFilters()">
        <option [ngValue]="null">{{ 'INSPECTION.FILTER.ALL_PROFESSORS' | translate }}</option>
        @for (p of options().professors; track p.id) {
          <option [ngValue]="p.id">{{ p.fullName }}</option>
        }
      </select>
    </label>

    <label class="ical-filter">
      <span class="ical-filter__label">{{ 'CALENDAR.FILTER.SUBJECT' | translate }}</span>
      <select [(ngModel)]="filterSubjectId" (ngModelChange)="applyFilters()">
        <option [ngValue]="null">{{ 'CALENDAR.FILTER.ALL_SUBJECTS' | translate }}</option>
        @for (g of subjectGroups(); track g.pathId) {
          <optgroup [label]="g.pathLabel || ('CALENDAR.CREATE.NO_PATH' | translate)">
            @for (s of g.subjects; track s.id) {
              <option [ngValue]="s.id">{{ subjectLabel(s) }}</option>
            }
          </optgroup>
        }
      </select>
    </label>

    <label class="ical-check">
      <input type="checkbox" [(ngModel)]="onlyTodo" (ngModelChange)="applyFilters()">
      {{ 'INSPECTION.CALENDAR.ONLY_TODO' | translate }}
    </label>

    @if (hasFilters) {
      <button type="button" class="ical__clear" (click)="clearFilters()">
        <i class="fa-solid fa-xmark"></i>
        {{ 'CALENDAR.FILTER.CLEAR' | translate }}
      </button>
    }
  </div>

  @if (joinError()) {
    <p class="ical__alert" role="alert">
      <i class="fa-solid fa-circle-exclamation"></i>
      <span>{{ joinError()! | translate }}</span>
    </p>
  }

  <!-- ─── La grille partagée, en mode inspection ──────────────────── -->
  @if (scopeIsEmpty()) {
    <!-- Le résumé du périmètre, au-dessus, dit déjà pourquoi l'agenda est vide. -->
  } @else {
    <app-sessions-calendar [sessions]="sessions()"
                           [loading]="loading()"
                           [errored]="errored()"
                           [inspection]="true"
                           (rangeChange)="onRange($event)"
                           (select)="onSelect($event)"
                           (retry)="reload()"></app-sessions-calendar>
  }

  <!-- ─── Fiche de séance : inspecter sans quitter l'agenda ────────── -->
  <dialog #detailDialog class="ical-dialog ical-drawer-host"
          (close)="onDetailClosed()"
          (mousedown)="onPressStart($event)"
          (click)="onBackdropClick($event, detailDialog)">
    @if (selected(); as s) {
    <aside class="ical-drawer" [class]="view.colorClass(s)" aria-labelledby="ical-detail-title">
      <button type="button" class="ical-drawer__close" (click)="closeDetail()"
              [attr.aria-label]="'CALENDAR.CLOSE' | translate">
        <i class="fa-solid fa-xmark"></i>
      </button>

      <span class="ical-drawer__kind">
        <i [class]="view.kindIcon($any(s.sessionKind))"></i>
        {{ view.kindLabelKey($any(s.sessionKind)) | translate }}
      </span>

      <h2 class="ical-drawer__title" id="ical-detail-title">{{ s.title }}</h2>

      <div class="ical-badges">
        @if (s.isOngoing) {
          <span class="ical-badge ical-badge--live">
            <i class="fa-solid fa-circle-dot"></i> {{ 'INSPECTION.STATE.ONGOING' | translate }}
          </span>
        }
        @if (s.isInspected) {
          <span class="ical-badge ical-badge--done">
            <i class="fa-solid fa-circle-check"></i> {{ 'INSPECTION.STATE.DONE' | translate }}
          </span>
        } @else if (s.status !== 'Cancelled') {
          <span class="ical-badge ical-badge--todo">
            <i class="fa-solid fa-clock"></i> {{ 'INSPECTION.STATE.TODO' | translate }}
          </span>
        }
        <span class="ical-badge">{{ ('ADMIN_CALENDAR.STATUS.' + s.status.toUpperCase()) | translate }}</span>
      </div>

      <ul class="ical-drawer__meta">
        <li><i class="fa-solid fa-calendar"></i> {{ start(s) | localizedDate:'EEEE dd MMMM yyyy' }}</li>
        <li>
          <i class="fa-solid fa-clock"></i>
          {{ time(s.startTime) }} – {{ time(s.endTime) }}
          <span class="ical-drawer__dim">({{ s.durationMinutes }} {{ 'CALENDAR.MINUTES' | translate }})</span>
        </li>
        @if (s.consultantName) { <li><i class="fa-solid fa-chalkboard-user"></i> {{ s.consultantName }}</li> }
      </ul>

      <!-- Niveau, matière, groupe, élèves : chacun sous son libellé. -->
      <app-session-audience [session]="s"></app-session-audience>

      <!-- Les rapports déjà déposés — les miens et ceux des autres inspecteurs. -->
      <section class="ical-block">
        <h3 class="ical-block__title"><i class="fa-solid fa-clipboard-check"></i> {{ 'INSPECTION.CALENDAR.REPORTS' | translate }}</h3>
        @if (s.inspections.length === 0) {
          <p class="ical-drawer__dim">{{ 'INSPECTION.CALENDAR.NO_REPORT' | translate }}</p>
        } @else {
          <ul class="ical-reports">
            @for (r of s.inspections; track r.id) {
              <li class="ical-report">
                <span class="ical-report__who">{{ r.inspectorName }}</span>
                <span class="ical-drawer__dim">{{ r.inspectedAt | date:'dd/MM/yyyy HH:mm' }}</span>
                @if (r.attachments.length) {
                  <span class="ical-drawer__dim"><i class="fa-solid fa-paperclip"></i> {{ r.attachments.length }}</span>
                }
              </li>
            }
          </ul>
        }
      </section>

      <!-- Les actes : rejoindre tant que ça se tient, consulter ensuite, rédiger toujours. -->
      <div class="ical-actions">
        @if (rules.canJoin(s)) {
          <button type="button" class="ical-btn" (click)="join(s)">
            <i class="fa-solid fa-video"></i> {{ 'INSPECTION.JOIN' | translate }}
          </button>
        } @else {
          @if (rules.playable(s); as rec) {
            <button type="button" class="ical-btn" (click)="openRecording(s)" [title]="rec.title">
              <i class="fa-solid fa-circle-play"></i> {{ 'INSPECTION.WATCH' | translate }}
            </button>
          } @else if (rules.ended(s) && s.hasMeeting) {
            <span class="ical-drawer__dim">{{ 'INSPECTION.LINK_EXPIRED' | translate }}</span>
          }
        }
        @if (!rules.inspectable(s)) {
          <span class="ical-drawer__dim">{{ 'INSPECTION.NOT_YET' | translate }}</span>
        }
        <button type="button" class="ical-btn ical-btn--primary" (click)="openReport(s)">
          <i class="fa-solid fa-pen-to-square"></i>
          {{ (s.isInspected ? 'INSPECTION.EDIT_REPORT' : 'INSPECTION.WRITE_REPORT') | translate }}
        </button>
      </div>
    </aside>
    }
  </dialog>

  <app-inspection-report-dialog [session]="target()"
                                (saved)="onReportSaved()"
                                (changed)="load()"
                                (closed)="target.set(null)"></app-inspection-report-dialog>

  <app-material-preview [item]="preview()" (closed)="preview.set(null)"></app-material-preview>
</div>
